import imageCompression from 'browser-image-compression';
import Compressor from 'compressorjs';
import { FILE_TYPE, FILE_UPLOAD_SIZE } from 'constants/common';
import Logger from 'utils/logger';

// TODO: Configure abort controller
// 2 Libraries are used to handle compression to support different file formats, browsers and other edge cases

export enum CompressionLibraries {
  BROWSER_IMAGE_COMPRESSION,
  COMPRESSOR_JS
}

const EXIF_STRIPABLE_TYPES = new Set<string>([FILE_TYPE.JPEG, FILE_TYPE.JPG, FILE_TYPE.PNG, FILE_TYPE.WEBP]);

const isExifStripableImage = (file: File): boolean => EXIF_STRIPABLE_TYPES.has(file.type);

const createFileFromBlob = (blob: Blob, fileName: string, fallbackType: string): File =>
  new File([blob], fileName, { type: blob.type || fallbackType });

export const handleImageCompression = async ({
  library,
  file,
  onCompressionInProgress = undefined
}: {
  library: CompressionLibraries;
  file: File;
  onCompressionInProgress?: (progress: number) => void;
}): Promise<File> => {
  if (library === CompressionLibraries.BROWSER_IMAGE_COMPRESSION) {
    return useBrowserImageCompression({ file, onCompressionInProgress });
  } else if (library === CompressionLibraries.COMPRESSOR_JS) {
    return useCompressorJS({ file });
  }
};

export const useBrowserImageCompression = async ({
  file,
  onCompressionInProgress = undefined
}: {
  file: File;
  onCompressionInProgress?: (progress: number) => void;
}): Promise<File> => {
  const imageFile = file;
  const options = {
    maxSizeMB: 1,
    alwaysKeepResolution: false,
    useWebWorker: true,
    maxIteration: 10,
    preserveExif: false,
    onProgress: onCompressionInProgress && onCompressionInProgress // the callback gets progress from 0 - 100
  };
  try {
    const compressedBlob = await imageCompression(imageFile, options);
    const compressedFile = new File([compressedBlob], compressedBlob.name, { type: compressedBlob.type });

    return compressedFile;
  } catch (error) {
    Logger.logException(error);
  }
};

export const useCompressorJS = async ({
  file,
  quality = 0.6,
  strict = false
}: {
  file: File;
  quality?: number;
  strict?: boolean;
}): Promise<File> => {
  const imageFile = file;
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality,
      convertSize: FILE_UPLOAD_SIZE.SIZE_1_MB,
      retainExif: false,
      checkOrientation: true,
      // When strict is true, CompressorJS may return the original file (with EXIF)
      // if the re-encoded blob is larger — disable that for metadata stripping.
      strict,
      success: compressedBlob => {
        const compressedFile = createFileFromBlob(compressedBlob, imageFile.name, imageFile.type);
        resolve(compressedFile);
      },
      error: err => {
        reject(err);
      }
    });
  });
};

/**
 * Re-encodes raster images so EXIF / GPS metadata is removed before upload.
 * Uses CompressorJS with retainExif: false and strict: false so the cleaned
 * file is always returned even when slightly larger than the original.
 */
export const stripExifFromImage = async (file: File): Promise<File> => {
  if (!isExifStripableImage(file)) {
    return file;
  }

  try {
    return await useCompressorJS({
      file,
      quality: 0.92,
      strict: false
    });
  } catch (error) {
    Logger.logException(error);
    return file;
  }
};

/**
 * Prepares an image for upload: always strips EXIF for stripable types,
 * and optionally compresses when the file is larger than 1MB.
 */
export const prepareImageForUpload = async ({
  file,
  allowImageCompression = true,
  onCompressionInProgress
}: {
  file: File;
  allowImageCompression?: boolean;
  onCompressionInProgress?: (progress: number) => void;
}): Promise<File> => {
  if (!file.type.startsWith('image/')) {
    return file;
  }

  const needsCompression = allowImageCompression && file.size > FILE_UPLOAD_SIZE.SIZE_1_MB;

  try {
    if (needsCompression) {
      if (file.type === FILE_TYPE.PNG) {
        return await useCompressorJS({ file, quality: 0.6, strict: false });
      }

      const compressedFile = await useBrowserImageCompression({ file, onCompressionInProgress });
      return compressedFile || (await stripExifFromImage(file));
    }

    return await stripExifFromImage(file);
  } catch (error) {
    Logger.logException(error);
    return file;
  }
};
